import {ObjectRef} from "./types";

/**
 * What a host screen can reach from a diagnostic, e.g. the driver activity grid open behind the dialog.
 *
 * @param navigableTypes object types the host navigates to, e.g. ["TTA", "TRANSPORT"]; empty means none
 * @param navigableIds   ids the host has loaded. When given, a ref must match one to be offered as a link, so a
 *                       report covering more than the grid doesn't show links that go nowhere.
 */
export type DiagnosticNavigationTarget = {
  navigableTypes?: string[];
  navigableIds?: string[];
};

export type DiagnosticNavigation = {
  canNavigate: (ref: ObjectRef) => boolean;
  onNavigate: (ref: ObjectRef) => void;
};

/** Every identity a ref can be recognized by: the emitter may hold the ticket id where the grid uses a transport id. */
const identities = (ref: ObjectRef): string[] => [
  ref.objectId,
  ref.context?.["pickupTransportId"],
  ref.context?.["deliveryTransportId"],
  ...(ref.resourceIdentifiers ?? []),
].filter(Boolean) as string[];

/**
 * Links a diagnostic's records to a host screen. The host gets back just an id; it built the id list, so the id
 * is enough for it to find the row again.
 */
export const createDiagnosticNavigation = (
  target: DiagnosticNavigationTarget | null | undefined,
  navigate: ((id: string) => void) | undefined,
): DiagnosticNavigation | undefined => {
  if (!target || !navigate) return undefined;

  const {navigableTypes, navigableIds} = target;

  return {
    canNavigate: (ref: ObjectRef) => {
      if (!ref?.objectType) return false;
      if (navigableTypes?.length && !navigableTypes.includes(ref.objectType)) return false;
      if (!navigableIds?.length) return Boolean(navigableTypes?.length);
      return identities(ref).some((candidate) => navigableIds.includes(candidate));
    },
    onNavigate: (ref: ObjectRef) => {
      const id = ref.objectId ?? ref.resourceIdentifiers?.[0];
      if (id) navigate(id);
    },
  };
};
